import React from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Formik, Form, useFormikContext } from 'formik'
import {
  Alert,
  AlertTitle,
  Box,
  Divider,
  Grid,
  useTheme,
  useMediaQuery,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Container,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Fab,
  TextField,
  Badge,
} from '@mui/material'
import CommentIcon from '@mui/icons-material/Comment'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import CheckIcon from '@mui/icons-material/Check'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import MapIcon from '@mui/icons-material/Map'
import NatureIcon from '@mui/icons-material/Nature'
import StraightenIcon from '@mui/icons-material/Straighten'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import WarningIcon from '@mui/icons-material/Warning'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { setItemsFormik } from '../TreeRegistrationForm/datasetFormik'
import { submitTreeRegistrationV2 } from '../../utils/formikCreate'
import { submitTreeUpdateV2 } from '../../utils/formikUpdate_v2'
import {
  mapTreeDataToFormikValues,
  mapTreeDataToAutocompleteState,
} from '../../utils/mapTreeDataToFormikValues'
import { uploadTreePhotos, resolveTreeRoute } from '../../services'
import { fetchTenantBoundary } from '../../services/tenantService'
import { detectCityFromURL } from '../../utils/cityDetector'
import { buildCantonUrl, buildTreePublicUrl } from '../../utils/cantonUrl'
import { isPointInBoundary } from '../../utils/geoBoundaryValidation'
import { useCities } from '../../context/CitiesContext'
import { useAuth } from '../../context/AuthProvider'
import { useTreePermissions } from '../../hooks/useTreePermissions'
import { TREE_PERMISSION } from '../../utils/treePermissions'
import { setTreeCoords, setAddMode } from '../../redux/slices/Map/map'
import {
  invalidateMapData,
  setEnableSpecieField,
  setEnableSiteField,
  setEnableAcronymField,
  setEnableSectionField,
  setEnablePseudonymField,
  saveTemporalFeatures,
} from '../../redux/slices/Tree/treeFeatures'

import { useMapContext } from '../../context/MapContext'
import LinearInderterminate from '../LinearIndeterminate'
import AlertCustom from '../Alerts'
import Backdrop from '../Backdrop'
import MapboxMap from '../MapboxGL'
import WizardProgressMobile from './WizardProgressMobile'
import WizardNavigationMobile from './WizardNavigationMobile'
import LocationStep from './steps/LocationStep'
import TreeDataStep from './steps/TreeDataStep'
import DasometricStep from './steps/DasometricStep'
import PhytosanitaryStep from './steps/PhytosanitaryStep'
import RiskStep from './steps/RiskStep'
import SummaryStep from './steps/SummaryStep'
import RegistrationStatusPanel, {
  REGISTRATION_STATUS,
  REGISTRATION_STATUS_LABELS,
  STATUS_META,
  cleanRevisionObservations,
  normalizeRegistrationStatus,
} from './components/RegistrationStatusPanel'
import {
  GENERAL_RISK_FIELD_FALLBACK,
  resolveGeneralRiskFieldName,
} from '../../utils/riskFieldHelpers'

const DRAFT_KEY = 'tree-registration-draft-v2'
const EDIT_DRAFT_KEY_PREFIX = `${DRAFT_KEY}-edit`
const DEFAULT_AUTOCOMPLETE_STATE = { error: false, selected: null }
const EMPTY_LIST = []

const toFiniteNumber = value => {
  if (value == null || value === '') return null
  const normalized = String(value).replace(',', '.').trim()
  const parsed = parseFloat(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

const isValidLngLat = (lng, lat) => {
  return (
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    lng >= -180 &&
    lng <= 180 &&
    lat >= -90 &&
    lat <= 90
  )
}

const getAttrValueByKeys = (attributes, keys) => {
  if (!Array.isArray(attributes) || attributes.length === 0) return null
  const normalizedKeys = keys.map(key => String(key).toLowerCase())
  const found = attributes.find(attr => {
    const attrKey = String(attr?.metaFeature?.key ?? '').toLowerCase()
    return normalizedKeys.includes(attrKey)
  })
  return found?.value ?? null
}

const extractLngLatFromTreeData = treeData => {
  if (!treeData) return null

  const attrs = treeData.attributes || []
  const attrLng = getAttrValueByKeys(attrs, ['longX', 'lng', 'longitude', 'lon'])
  const attrLat = getAttrValueByKeys(attrs, ['latY', 'lat', 'latitude'])

  const geometryCoords = Array.isArray(treeData.geometry?.coordinates)
    ? treeData.geometry.coordinates
    : Array.isArray(treeData.coordinates)
      ? treeData.coordinates
      : Array.isArray(treeData.location?.coordinates)
        ? treeData.location.coordinates
        : null

  const candidates = [
    [attrLng, attrLat],
    [treeData.longX, treeData.latY],
    [treeData.lng, treeData.lat],
    [treeData.longitude, treeData.latitude],
    [treeData.lon, treeData.lat],
    geometryCoords ? [geometryCoords[0], geometryCoords[1]] : [null, null],
  ]

  for (const [rawLng, rawLat] of candidates) {
    const lng = toFiniteNumber(rawLng)
    const lat = toFiniteNumber(rawLat)
    if (isValidLngLat(lng, lat)) {
      return [lng, lat]
    }
  }

  return null
}
const STEPS = [
  { label: 'Ubicación', icon: <MapIcon />, description: 'Geolocalización y sitio' },
  { label: 'Datos del Árbol', icon: <NatureIcon />, description: 'Especie y estado' },
  {
    label: 'Datos Dasométricos',
    icon: <StraightenIcon />,
    description: 'Medidas físicas',
  },
  {
    label: 'Estado Fitosanitario',
    icon: <HealthAndSafetyIcon />,
    description: 'Salud del árbol',
  },
  {
    label: 'Evaluación de Riesgos',
    icon: <WarningIcon />,
    description: 'Riesgos potenciales',
  },
  { label: 'Resumen', icon: <AssignmentIcon />, description: 'Confirmar registro' },
]
const TOTAL_STEPS = STEPS.length

const MIN_PHOTOS = 5
const MAX_PHOTOS = 6

const DraftSync = ({
  draftLoaded,
  draftKey,
  activeStep,
  completedSteps,
  groupFieldAutocomplete,
  mode,
}) => {
  const { values } = useFormikContext()

  React.useEffect(() => {
    if (!draftLoaded || mode === 'edit' || !draftKey) return
    const draft = {
      values,
      autocomplete: {
        specieField: groupFieldAutocomplete.specieField,
        siteField: groupFieldAutocomplete.siteField,
        siteAcronym: groupFieldAutocomplete.siteAcronym,
        sectionField: groupFieldAutocomplete.sectionField,
        pseudonymField: groupFieldAutocomplete.pseudonymField,
      },
      activeStep,
      completedSteps,
      savedAt: new Date().toISOString(),
    }
    localStorage.setItem(draftKey, JSON.stringify(draft))
  }, [
    values,
    draftLoaded,
    draftKey,
    mode,
    activeStep,
    completedSteps,
    groupFieldAutocomplete,
  ])

  return null
}

const UnsavedChangesSync = ({
  mode,
  coords,
  photos,
  deletedPhotoIds,
  onUnsavedChange,
}) => {
  const { dirty } = useFormikContext()

  React.useEffect(() => {
    const hasCapturedCoords = coords?.lng != null && coords?.lat != null
    const hasNewPhotos =
      mode === 'edit'
        ? photos.some(photo => !photo?.isExisting)
        : Array.isArray(photos) && photos.length > 0
    const hasDeletedPhotos = mode === 'edit' && deletedPhotoIds.length > 0

    onUnsavedChange(
      Boolean(dirty || hasCapturedCoords || hasNewPhotos || hasDeletedPhotos),
    )
  }, [dirty, mode, coords?.lng, coords?.lat, photos, deletedPhotoIds, onUnsavedChange])

  return null
}

const CoordinatesTenantValidationSync = ({
  selectedTenant,
  tenantBoundaryData,
  tenantBoundaryLoading,
  tenantIsGeneric,
  onStatusChange,
}) => {
  const { values } = useFormikContext()

  React.useEffect(() => {
    const lng = parseFloat(values.longX)
    const lat = parseFloat(values.latY)
    const hasValidCoords = !isNaN(lng) && lng !== 0 && !isNaN(lat) && lat !== 0

    if (!selectedTenant || !hasValidCoords || tenantBoundaryLoading || tenantIsGeneric) {
      onStatusChange(false)
      return
    }

    if (!tenantBoundaryData) {
      onStatusChange(false)
      return
    }

    onStatusChange(!isPointInBoundary([lng, lat], tenantBoundaryData))
  }, [
    values.longX,
    values.latY,
    selectedTenant,
    tenantBoundaryData,
    tenantBoundaryLoading,
    tenantIsGeneric,
    onStatusChange,
  ])

  return null
}

const TreeRegistrationWizardV2 = ({
  mode = 'create',
  treeData = null,
  existingPhotos = [],
  treeInactive = false,
}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // State
  const alertRef = React.useRef()
  const backdropRef = React.useRef()
  const formikRef = React.useRef()
  const treeCodeRef = React.useRef(null)
  const pendingPhotoTreeRef = React.useRef(null)
  const createdTreeErrorRef = React.useRef(null)
  const hasRestoredEdit = React.useRef(false)
  const hasFlyToEditRef = React.useRef(false)
  const restoredFromDraftRef = React.useRef(false)

  const [formik, setFormik] = React.useState(null)
  const [isStored, setIsStored] = React.useState(null)
  const [activeStep, setActiveStep] = React.useState(0)
  const [completedSteps, setCompletedSteps] = React.useState(
    new Array(TOTAL_STEPS).fill(false),
  )
  const [draftLoaded, setDraftLoaded] = React.useState(false)
  const [hasRestored, setHasRestored] = React.useState(false)
  const [photos, setPhotos] = React.useState(() =>
    mode === 'edit' ? existingPhotos : [],
  )
  const [primaryPhotoIndex, setPrimaryPhotoIndex] = React.useState(0)
  const [inconvenienceErrors, setInconvenienceErrors] = React.useState({})
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [missingFieldsOpen, setMissingFieldsOpen] = React.useState(false)
  const [missingFields, setMissingFields] = React.useState([])
  const [commitDialogOpen, setCommitDialogOpen] = React.useState(false)
  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false)
  const [exitConfirmOpen, setExitConfirmOpen] = React.useState(false)
  const [hasUnsavedData, setHasUnsavedData] = React.useState(false)
  const [deletedPhotoIds, setDeletedPhotoIds] = React.useState([])
  const [changesConfirmOpen, setChangesConfirmOpen] = React.useState(false)
  const [changedFields, setChangedFields] = React.useState([])
  const [photoWarningOpen, setPhotoWarningOpen] = React.useState(false)
  const [tenantBoundaryData, setTenantBoundaryData] = React.useState(null)
  const [tenantBoundaryLoading, setTenantBoundaryLoading] = React.useState(false)
  const [tenantIsGeneric, setTenantIsGeneric] = React.useState(false)
  const [isTenantCoordsOutOfBounds, setIsTenantCoordsOutOfBounds] = React.useState(false)
  const recorderDefaultedRef = React.useRef(false)
  const hasUnsavedDataRef = React.useRef(false)
  const tenantBoundaryCacheRef = React.useRef(new Map())
  const originalAutocompleteRef = React.useRef(null)

  const marginMenu = useSelector(state => state.drawerLeft.drawerHeight)
  const topBarMinHeight = useSelector(state => state.drawerLeft.drawerMinHeight)
  const progressStickyTop = `calc(max(${topBarMinHeight}, ${marginMenu}) + 0px)`

  const getToken = () => `Bearer ${window.localStorage.getItem('ArbolEc_token')}`

  const { selectedTenant, getTenantsByCantonSlug, getEnabledCities, getEnabledCantones } =
    useCities()
  const { isAdmin } = useAuth()
  const { canActInCanton } = useTreePermissions()
  const cantones = getEnabledCantones()

  const { temporal, enableSaveButton: groupFieldAutocomplete } = useSelector(
    state => state.treeFeatures,
  )
  const coords = useSelector(state => state.mapState.coords)
  const username = useSelector(state => state.login.username)
  const authUser = useSelector(state => state.login.authenticated?.user)

  const currentCity = detectCityFromURL(location.pathname) || 'loja'
  const { map, mapLoaded } = useMapContext()

  React.useEffect(() => {
    if (mode !== 'create' || isAdmin || !Array.isArray(authUser?.rol)) return
    if (!formikRef.current) return
    const values = formikRef.current.values || {}
    if (
      values.estadoRegistro === REGISTRATION_STATUS.NO_VERIFICADO &&
      (values.treeVerified_ === 0 || values.treeVerified_ === '0') &&
      !values.observacionesRevision?.length
    ) {
      return
    }
    formikRef.current.setFieldValue('estadoRegistro', REGISTRATION_STATUS.NO_VERIFICADO)
    formikRef.current.setFieldValue('treeVerified_', 0)
    formikRef.current.setFieldValue('observacionesRevision', [])
  }, [authUser?.rol, isAdmin, mode, draftLoaded])

  const resolveCantonSlugByTenant = React.useCallback(
    tenantSlug => {
      if (!tenantSlug) return null
      const allCities = getEnabledCities()
      for (const city of allCities) {
        const hasTenant = getTenantsByCantonSlug(city.slug).some(t => t.slug === tenantSlug)
        if (hasTenant) return city.slug
      }
      return null
    },
    [getEnabledCities, getTenantsByCantonSlug],
  )

  const editTreeFixedCoords = React.useMemo(() => {
    if (mode !== 'edit' || !treeData) return null
    return extractLngLatFromTreeData(treeData)
  }, [mode, treeData])

  const activeDraftKey = React.useMemo(() => {
    if (mode !== 'edit') return DRAFT_KEY
    const treeId = treeData?.id ?? treeData?.code ?? treeData?.codigo
    return treeId ? `${EDIT_DRAFT_KEY_PREFIX}-${treeId}` : null
  }, [mode, treeData?.id, treeData?.code, treeData?.codigo])

  React.useEffect(() => {
    if (!selectedTenant) {
      setTenantBoundaryData(null)
      setTenantBoundaryLoading(false)
      setTenantIsGeneric(false)
      setIsTenantCoordsOutOfBounds(false)
      return
    }

    if (tenantBoundaryCacheRef.current.has(selectedTenant)) {
      const cached = tenantBoundaryCacheRef.current.get(selectedTenant)
      if (cached?.es_generico) {
        setTenantIsGeneric(true)
        setTenantBoundaryData(null)
      } else {
        setTenantIsGeneric(false)
        setTenantBoundaryData(cached)
      }
      setTenantBoundaryLoading(false)
      return
    }

    // Verificar si el tenant es genérico según los datos locales (sin esperar API)
    const localTenants = getTenantsByCantonSlug(currentCity)
    const localTenant = localTenants.find(t => t.slug === selectedTenant)
    if (localTenant?.isGeneric) {
      setTenantIsGeneric(true)
      setTenantBoundaryData(null)
      setTenantBoundaryLoading(false)
      setIsTenantCoordsOutOfBounds(false)
      tenantBoundaryCacheRef.current.set(selectedTenant, { es_generico: true })
      return
    }

    let cancelled = false
    setTenantBoundaryLoading(true)
    setTenantBoundaryData(null)
    setTenantIsGeneric(false)
    setIsTenantCoordsOutOfBounds(false)

    fetchTenantBoundary(selectedTenant)
      .then(geojson => {
        if (cancelled) return
        if (geojson?.es_generico) {
          setTenantIsGeneric(true)
          setTenantBoundaryData(null)
          tenantBoundaryCacheRef.current.set(selectedTenant, { es_generico: true })
        } else {
          const normalizedGeojson = geojson?.type ? geojson : null
          setTenantIsGeneric(false)
          setTenantBoundaryData(normalizedGeojson)
          if (normalizedGeojson) {
            tenantBoundaryCacheRef.current.set(selectedTenant, normalizedGeojson)
          }
        }
      })
      .finally(() => {
        if (!cancelled) setTenantBoundaryLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedTenant, currentCity, getTenantsByCantonSlug])

  const resetAutocompleteState = React.useCallback(() => {
    dispatch(setEnableSpecieField(DEFAULT_AUTOCOMPLETE_STATE))
    dispatch(setEnableSiteField(DEFAULT_AUTOCOMPLETE_STATE))
    dispatch(setEnableAcronymField(DEFAULT_AUTOCOMPLETE_STATE))
    dispatch(setEnableSectionField(DEFAULT_AUTOCOMPLETE_STATE))
    dispatch(setEnablePseudonymField(DEFAULT_AUTOCOMPLETE_STATE))
  }, [dispatch])

  const persistDraft = React.useCallback(() => {
    if (!activeDraftKey || !formikRef.current?.values) return false
    const draft = {
      values: formikRef.current.values,
      autocomplete: {
        specieField: groupFieldAutocomplete.specieField,
        siteField: groupFieldAutocomplete.siteField,
        siteAcronym: groupFieldAutocomplete.siteAcronym,
        sectionField: groupFieldAutocomplete.sectionField,
        pseudonymField: groupFieldAutocomplete.pseudonymField,
      },
      activeStep,
      completedSteps,
      mode,
      treeId: treeData?.id ?? null,
      savedAt: new Date().toISOString(),
    }
    localStorage.setItem(activeDraftKey, JSON.stringify(draft))
    return true
  }, [
    activeDraftKey,
    groupFieldAutocomplete,
    activeStep,
    completedSteps,
    mode,
    treeData?.id,
  ])

  const clearDraft = React.useCallback(() => {
    if (!activeDraftKey) return
    localStorage.removeItem(activeDraftKey)
  }, [activeDraftKey])

  const exitTarget = React.useCallback(
    () => {
      // Quien no puede actualizar en este cantón rebotaría en el guard de /editar-arbol: va al mapa
      if (mode === 'edit' || !canActInCanton(TREE_PERMISSION.UPDATE, currentCity)) {
        return buildCantonUrl(currentCity, cantones)
      }
      return buildCantonUrl(currentCity, cantones, '/editar-arbol')
    },
    [currentCity, cantones, mode, canActInCanton],
  )

  const leaveForm = React.useCallback(
    ({ keepDraft }) => {
      if (keepDraft) persistDraft()
      else clearDraft()

      dispatch(setAddMode(false))
      dispatch(setTreeCoords(null))
      resetAutocompleteState()

      setExitConfirmOpen(false)
      setHasUnsavedData(false)
      hasUnsavedDataRef.current = false

      navigate(exitTarget(), { replace: true })
    },
    [
      persistDraft,
      clearDraft,
      dispatch,
      resetAutocompleteState,
      navigate,
      exitTarget,
    ],
  )

  const openExitDialog = React.useCallback(() => {
    setExitConfirmOpen(true)
  }, [])

  const requestExit = React.useCallback(
    () => {
      if (hasUnsavedDataRef.current) {
        openExitDialog()
        return
      }
      leaveForm({ keepDraft: false })
    },
    [openExitDialog, leaveForm],
  )

  const handleUnsavedChange = React.useCallback(nextValue => {
    hasUnsavedDataRef.current = nextValue
    setHasUnsavedData(prev => (prev === nextValue ? prev : nextValue))
  }, [])

  // Al salir del formulario (por cualquier ruta), limpiar captura para evitar marker/anillos fantasmas.
  React.useEffect(() => {
    return () => {
      dispatch(setAddMode(false))
      dispatch(setTreeCoords(null))
    }
  }, [dispatch])

  // Protección para cierre/recarga del navegador (en hard-navigation no se permite modal custom).
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handleBeforeUnload = event => {
      if (!hasUnsavedDataRef.current) return
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // Resize map when returning to step 0 or when layout changes (mobile ↔ desktop)
  React.useEffect(() => {
    if (activeStep === 0 && map && mapLoaded) {
      const timer = setTimeout(() => {
        try {
          map.resize()
        } catch (e) {
          /* ignore */
        }
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [activeStep, isMobile, map, mapLoaded])

  // Initialization Effects
  React.useEffect(() => {
    // In edit mode, pass treeData so inconveniencesItems pre-fills category 1/7 fields
    const initData = mode === 'edit' && treeData ? { properties: treeData } : null
    setItemsFormik(initData, { canton: currentCity }).then(variables => {
      setFormik(variables)
      if (variables?.ITEMS_INCONVENIENCE_FORMIK) {
        dispatch(saveTemporalFeatures(variables.ITEMS_INCONVENIENCE_FORMIK))
      }
    })
    return () => setFormik(null)
  }, [currentCity, dispatch, mode, treeData])

  // Draft restore effect
  React.useEffect(() => {
    if (!formik || !formikRef.current || hasRestored) return
    const raw = activeDraftKey ? localStorage.getItem(activeDraftKey) : null
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        const sameTree =
          mode !== 'edit' ||
          !parsed?.treeId ||
          !treeData?.id ||
          String(parsed.treeId) === String(treeData.id)

        if (sameTree) {
          restoredFromDraftRef.current = true
          if (mode === 'edit') hasRestoredEdit.current = true
          const restoredValues = {
            ...formik.INITIAL_FORMIK_STATE,
            ...(parsed?.values || {}),
          }
          formikRef.current.setValues(restoredValues)
          const autocomplete = parsed?.autocomplete || {}
          if (autocomplete.specieField)
            dispatch(setEnableSpecieField(autocomplete.specieField))
          if (autocomplete.siteField) dispatch(setEnableSiteField(autocomplete.siteField))
          if (autocomplete.siteAcronym)
            dispatch(setEnableAcronymField(autocomplete.siteAcronym))
          if (autocomplete.sectionField)
            dispatch(setEnableSectionField(autocomplete.sectionField))
          if (autocomplete.pseudonymField)
            dispatch(setEnablePseudonymField(autocomplete.pseudonymField))
          if (Array.isArray(parsed?.completedSteps))
            setCompletedSteps(parsed.completedSteps)
          if (Number.isInteger(parsed?.activeStep)) setActiveStep(parsed.activeStep)
        }
      } catch (err) {
        console.warn('No se pudo restaurar el borrador:', err)
      }
    }
    setDraftLoaded(true)
    setHasRestored(true)
  }, [formik, hasRestored, mode, dispatch, activeDraftKey, treeData?.id])

  // Edit mode: restore autocomplete state from treeData
  React.useEffect(() => {
    if (mode !== 'edit' || !formik || !treeData || hasRestoredEdit.current) return
    if (restoredFromDraftRef.current) return
    hasRestoredEdit.current = true

    const autocompleteState = mapTreeDataToAutocompleteState(treeData)
    if (autocompleteState.specieField)
      dispatch(setEnableSpecieField(autocompleteState.specieField))
    if (autocompleteState.siteField)
      dispatch(setEnableSiteField(autocompleteState.siteField))
    if (autocompleteState.siteAcronym)
      dispatch(setEnableAcronymField(autocompleteState.siteAcronym))
    if (autocompleteState.sectionField)
      dispatch(setEnableSectionField(autocompleteState.sectionField))
    if (autocompleteState.pseudonymField)
      dispatch(setEnablePseudonymField(autocompleteState.pseudonymField))
  }, [formik, mode, treeData, dispatch])

  // Store original autocomplete state from treeData once (used for changed-fields detection)
  React.useEffect(() => {
    if (mode !== 'edit' || !treeData || originalAutocompleteRef.current) return
    originalAutocompleteRef.current = mapTreeDataToAutocompleteState(treeData)
  }, [mode, treeData])

  // Edit mode: fly to tree location without setting coords (avoids showing the capture marker)
  React.useEffect(() => {
    if (mode !== 'edit' || !treeData || !map || !mapLoaded || hasFlyToEditRef.current)
      return
    const coords = extractLngLatFromTreeData(treeData)
    if (coords) {
      hasFlyToEditRef.current = true
      map.flyTo({
        center: coords,
        zoom: 18,
        essential: true,
      })
    }
  }, [mode, treeData, map, mapLoaded])

  React.useEffect(() => {
    if (!coords || !formikRef.current) return
    formikRef.current.setFieldValue('longX', coords.lng?.toString() ?? '')
    formikRef.current.setFieldValue('latY', coords.lat?.toString() ?? '')
  }, [coords])

  React.useEffect(() => {
    if (!isStored) return
    if (isStored.stored === true) {
      const successMsg =
        mode === 'edit'
          ? 'Árbol actualizado correctamente'
          : 'Árbol registrado correctamente'
      alertRef?.current?.message(successMsg)
      alertRef?.current?.isSeverity('success')
      alertRef?.current?.openAlert(true)
      if (mode === 'edit') {
        dispatch(invalidateMapData())
        const backendResolvedCanton = isStored?.targetCantonSlug || null
        const currentCityHasTenant = selectedTenant
          ? getTenantsByCantonSlug(currentCity).some(t => t.slug === selectedTenant)
          : false
        const targetCantonSlug =
          backendResolvedCanton ||
          (currentCityHasTenant ? currentCity : resolveCantonSlugByTenant(selectedTenant)) ||
          currentCity

        const fallbackTreeCode = treeData?.codigo || treeData?.code || null
        const cantonPath = buildCantonUrl(targetCantonSlug, cantones)
        const fallbackTreePath = buildTreePublicUrl(targetCantonSlug, cantones, fallbackTreeCode)

        const navigateToTree = async () => {
          let targetPath = fallbackTreePath
          try {
            const resolved = await resolveTreeRoute(isStored.treeId)
            if (resolved?.found && resolved.paths?.public) {
              targetPath = resolved.paths.public
            } else if (!resolved?.serverError) {
              targetPath = cantonPath
            }
          } catch {
            // fallbackTreePath ya asignado
          }
          navigate(targetPath, { replace: true })
        }

        const timeoutId = setTimeout(() => {
          void navigateToTree()
        }, 1500)

        return () => clearTimeout(timeoutId)
      } else {
        setTimeout(() => backdropRef?.current?.openAlert(true), 500)
      }
    } else {
      alertRef?.current?.message(
        isStored.message || 'Error al procesar el árbol. Inténtelo nuevamente.',
      )
      alertRef?.current?.isSeverity('error')
      alertRef?.current?.openAlert(true)
    }
  }, [
    isStored,
    mode,
    dispatch,
    navigate,
    currentCity,
    cantones,
    selectedTenant,
    getTenantsByCantonSlug,
    resolveCantonSlugByTenant,
    treeData?.codigo,
    treeData?.code,
  ])

  const defaultRecorderName = React.useMemo(() => {
    const fullName = [authUser?.nombre, authUser?.apellido]
      .filter(Boolean)
      .join(' ')
      .trim()
    return fullName || username || authUser?.email || ''
  }, [authUser?.nombre, authUser?.apellido, authUser?.email, username])

  React.useEffect(() => {
    if (!draftLoaded) return
    if (!defaultRecorderName) return
    if (!formikRef.current) return
    const current = formikRef.current.values?.recorded_
    if (current && current.toString().trim()) return
    if (recorderDefaultedRef.current) return
    formikRef.current.setFieldValue('recorded_', defaultRecorderName)
    recorderDefaultedRef.current = true
  }, [draftLoaded, defaultRecorderName])

  const itemsInconvenience = formik?.ITEMS_INCONVENIENCE_FORMIK || EMPTY_LIST
  const generalRiskFieldName = React.useMemo(
    () => resolveGeneralRiskFieldName(itemsInconvenience, GENERAL_RISK_FIELD_FALLBACK),
    [itemsInconvenience],
  )

  if (!Boolean(formik)) return <LinearInderterminate />

  // Fields Setup
  const zoneTypesData = formik.ZONE_TYPES_DATA || {}
  const allSiteTypes = formik.ALL_SITE_TYPES || {}
  const siteTypesData = formik.SITE_TYPES_DATA || {}

  const initialValues = (() => {
    const base = {
      ...formik.INITIAL_FORMIK_STATE,
      bifurcaciones: '1',
      codigoManual: '',
      prioridadAtencion: '',
      modoCodigoPlusCode: true,
      codeAction: null,
      referenciaUbicacion_: '',
      estadoRegistro: REGISTRATION_STATUS.NO_VERIFICADO,
      observacionesRevision: [],
      recorded_: formik.INITIAL_FORMIK_STATE.recorded_ || defaultRecorderName || '',
    }
    if (mode === 'edit' && treeData) {
      return mapTreeDataToFormikValues(treeData, base, zoneTypesData, siteTypesData)
    }
    return base
  })()

  const itemsGeneral = formik.ITEMS_GENERAL_FORMIK || []
  const itemsAttributes = formik.ITEMS_ATTRIBUTES_FORMIK || []

  // Validation Logic
  const STEP_FORMIK_FIELDS = {
    0: [
      'longX',
      'latY',
      'typeSite_',
      'sitePlace_',
      'site_',
      'altitud',
      'referenciaUbicacion_',
    ],
    1: ['alive_', 'treeVerified_'],
    2: [
      'dap',
      'cap',
      'ht',
      'hc',
      'copaNS',
      'copaEW',
      'bifurcaciones',
      'inclArbol',
      'compInclArbol',
      'fusteRectitud',
      'espaciamiento',
    ],
    3: [
      'follaje',
      'madurez',
      'floracion',
      'fructificacion',
      'calidadRaiz',
      'calidadFuste',
      'calidadCorteza',
      'calidadRamas',
      'calidadHojas',
      'calidadCima',
      'calidadCopa',
    ],
    4: [
      generalRiskFieldName,
      'rsgoCaida',
      'rsgoAfeccionConstr',
      'rsgoInterfCirculacion',
      'rsgoInfraestructura',
      'rsgoRedesElectricas',
      'prioridadAtencion',
    ],
    5: ['estadoRegistro', 'observacionesRevision'],
  }

  const FIELD_LABELS = {
    coords: 'Coordenadas (ubicación en mapa)',
    tenant: 'Institución (tenant)',
    tenantBoundary: 'Coordenadas dentro de los límites del tenant seleccionado',
    typeSite_: 'Tipo de zona',
    sitePlace_: 'Lugar del sitio',
    site_: 'Sitio',
    referenciaUbicacion_: 'Referencia de ubicación',
    siteAcronym: 'Acrónimo del sitio',
    sectionField: 'Sección',
    specie: 'Especie',
    pseudonyms: 'Nombres comunes (sinónimos)',
    alive_: '¿Árbol vivo?',
    treeVerified_: '¿Árbol verificado?',
    estadoRegistro: 'Estado de registro',
    observacionesRevision: 'Correcciones',
    cap: 'CAP (circunferencia)',
    dap: 'DAP (diámetro)',
    ht: 'Altura total (HT)',
    hc: 'Altura comercial (HC)',
    copaNS: 'Copa Norte-Sur',
    copaEW: 'Copa Este-Oeste',
    bifurcaciones: 'Bifurcaciones',
    inclArbol: 'Inclinación del fuste principal (grados)',
    compInclArbol: 'Compensación de inclinación',
    fusteRectitud: 'Rectitud del fuste',
    espaciamiento: 'Espaciamiento',
    follaje: 'Follaje (%)',
    madurez: 'Madurez',
    floracion: 'Floración',
    fructificacion: 'Fructificación',
    calidadRaiz: 'Calidad de raíz',
    calidadFuste: 'Calidad de fuste',
    calidadCorteza: 'Calidad de corteza',
    calidadRamas: 'Calidad de ramas',
    calidadHojas: 'Calidad de hojas',
    calidadCima: 'Calidad de cima',
    calidadCopa: 'Calidad de copa',
    diseaseLocation: 'Ubicación de enfermedades',
    pestLocation: 'Ubicación de plagas',
    rsgoCaida: 'Riesgo de caída',
    rsgoAfeccionConstr: 'Afección por construcciones',
    rsgoInterfCirculacion: 'Interferencia con circulación',
    rsgoInfraestructura: 'Riesgo de infraestructura',
    rsgoRedesElectricas: 'Redes eléctricas',
    generalRisk: 'Riesgo general',
    prioridadAtencion: 'Urgencia de intervención',
  }

  const validateCoordinates = (longX, latY) => {
    const lng = parseFloat(longX)
    const lat = parseFloat(latY)
    return (
      !isNaN(lng) &&
      lng !== 0 &&
      !isNaN(lat) &&
      lat !== 0 &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    )
  }

  const handleTenantBoundsStatusChange = isOutOfBounds => {
    setIsTenantCoordsOutOfBounds(prev => (prev === isOutOfBounds ? prev : isOutOfBounds))
  }

  const showBoundaryAlert = message => {
    alertRef?.current?.message(message)
    alertRef?.current?.isSeverity('warning')
    alertRef?.current?.openAlert(true)
  }

  const validateCoordinatesAgainstTenant = async (
    longX,
    latY,
    { showAlert = false } = {},
  ) => {
    const lng = parseFloat(longX)
    const lat = parseFloat(latY)
    const hasValidCoords = !isNaN(lng) && lng !== 0 && !isNaN(lat) && lat !== 0

    if (!selectedTenant || !hasValidCoords) {
      setIsTenantCoordsOutOfBounds(false)
      return { isValid: true }
    }

    if (tenantBoundaryLoading) {
      const message = 'Cargando límites del tenant, intente de nuevo en un momento...'
      if (showAlert) showBoundaryAlert(message)
      setIsTenantCoordsOutOfBounds(false)
      return { isValid: false, message }
    }

    if (tenantIsGeneric) {
      setIsTenantCoordsOutOfBounds(false)
      return { isValid: true }
    }

    if (!tenantBoundaryData) {
      const message =
        'El tenant no tiene límites geográficos definidos. No se puede registrar árboles.'
      if (showAlert) showBoundaryAlert(message)
      setIsTenantCoordsOutOfBounds(false)
      return { isValid: false, message }
    }

    const isInsideTenant = isPointInBoundary([lng, lat], tenantBoundaryData)
    setIsTenantCoordsOutOfBounds(!isInsideTenant)

    if (!isInsideTenant) {
      const message = 'Este punto está fuera de los límites del tenant asignado'
      if (showAlert) showBoundaryAlert(message)
      return { isValid: false, message }
    }

    return { isValid: true }
  }

  const validateStep = async step => {
    if (!formikRef.current) return false
    const values = formikRef.current.values
    const errors = await formikRef.current.validateForm()
    const stepFields = STEP_FORMIK_FIELDS[step] || []

    // Check specific logic per step
    if (step === 0) {
      const { siteField, siteAcronym, sectionField } = groupFieldAutocomplete
      const hasSite = Boolean(siteField?.selected?.id || siteField?.selected)
      const hasTenant = Boolean(selectedTenant)
      const coordsValid = validateCoordinates(values.longX, values.latY)
      const hasZoneType = Boolean(values.typeSite_)
      const hasSitePlace = Boolean(values.sitePlace_)

      const hasReferenciaUbicacion = Boolean(values.referenciaUbicacion_?.trim())

      if (
        !hasSite ||
        !hasTenant ||
        !coordsValid ||
        !hasZoneType ||
        !hasSitePlace ||
        !hasReferenciaUbicacion
      )
        return false

      // In structured mode, acronym and section are required
      if (!values.modoCodigoPlusCode) {
        const hasSiteAcronym = Boolean(siteAcronym?.selected?.id || siteAcronym?.selected)
        const hasSection = Boolean(sectionField?.selected?.id || sectionField?.selected)
        if (!hasSiteAcronym || !hasSection) return false
      }

      const tenantBoundaryValidation = await validateCoordinatesAgainstTenant(
        values.longX,
        values.latY,
        { showAlert: true },
      )
      if (!tenantBoundaryValidation.isValid) return false
    }

    if (step === 1) {
      const { specieField, pseudonymField } = groupFieldAutocomplete
      const hasSpecie = Boolean(specieField?.selected?.id || specieField?.selected)
      const hasPseudonym = Boolean(pseudonymField?.selected?.length)
      if (!hasSpecie || !hasPseudonym) return false
    }

    if (step === 2) {
      const required = [
        'cap',
        'ht',
        'dap',
        'hc',
        'bifurcaciones',
        'inclArbol',
        'compInclArbol',
        'fusteRectitud',
        'espaciamiento',
      ]
      const hasRequired = required.every(name =>
        Boolean(values?.[name]?.toString().trim()),
      )
      if (!hasRequired) return false
    }

    if (step === 3) {
      const catErrors = {}

      // Ubicación enfermedades (3) obligatoria solo si hay presencia (2)
      const hasDiseasePresence = temporal.some(item => item.category === 2)
      if (hasDiseasePresence && !temporal.some(item => item.category === 3)) {
        catErrors[3] = true
      }

      // Ubicación plagas (5) obligatoria solo si hay presencia (4)
      const hasPestPresence = temporal.some(item => item.category === 4)
      if (hasPestPresence && !temporal.some(item => item.category === 5)) {
        catErrors[5] = true
      }

      // Validar que chips "otros" tengan texto especificado (solo categorías fitosanitarias 2-6)
      const OTHERS_IDS = [86, 87, 88, 89, 90, 91, 92]
      const PHYTO_CATEGORIES = [2, 3, 4, 5, 6]
      const othersWithoutText = temporal.filter(
        item =>
          PHYTO_CATEGORIES.includes(item.category) &&
          OTHERS_IDS.includes(item.typecategory) &&
          !item.initvalue?.trim(),
      )
      othersWithoutText.forEach(item => {
        catErrors[item.category] = true
      })

      // Validar campos formik del paso (follaje, fenología, calidad)
      const missingFormik = stepFields.filter(name => {
        const val = values[name]
        return val === undefined || val === null || val === ''
      })
      if (missingFormik.length > 0) {
        formikRef.current.setTouched(
          missingFormik.reduce((acc, name) => ({ ...acc, [name]: true }), {}),
        )
      }

      setInconvenienceErrors(catErrors)
      return Object.keys(catErrors).length === 0 && missingFormik.length === 0
    }

    if (step === 4) {
      const missingFormik = stepFields.filter(name => {
        const val = values[name]
        return val === undefined || val === null || val === ''
      })
      if (missingFormik.length > 0) {
        formikRef.current.setTouched(
          missingFormik.reduce((acc, name) => ({ ...acc, [name]: true }), {}),
        )
      }

      const hasStepErrors = stepFields.some(name => errors[name])
      if (hasStepErrors) {
        formikRef.current.setTouched(
          stepFields.reduce((acc, name) => ({ ...acc, [name]: true }), {}),
        )
      }

      return !hasStepErrors && missingFormik.length === 0
    }

    const hasStepErrors = stepFields.some(name => errors[name])
    if (hasStepErrors) {
      formikRef.current.setTouched(
        stepFields.reduce((acc, name) => ({ ...acc, [name]: true }), {}),
      )
    }

    return !hasStepErrors
  }

  // Collect all missing required fields across all steps (silent, no side effects)
  const collectAllMissingFields = () => {
    if (!formikRef.current) return []
    const values = formikRef.current.values
    const missing = []

    // En "En revisión" el admin solo anota correcciones para el técnico de campo:
    // se omiten los obligatorios del árbol y se exige únicamente ≥1 corrección.
    const isReviewStatus =
      !(mode === 'create' && !isAdmin) &&
      normalizeRegistrationStatus(values.estadoRegistro, values.treeVerified_) ===
        REGISTRATION_STATUS.EN_REVISION

    if (isReviewStatus) {
      if (cleanRevisionObservations(values.observacionesRevision).length === 0) {
        missing.push({
          step: TOTAL_STEPS - 1,
          fieldKey: 'observacionesRevision',
          label: FIELD_LABELS.observacionesRevision,
        })
      }
      return missing
    }

    // Step 0 — Ubicación
    const { siteField, siteAcronym, sectionField } = groupFieldAutocomplete
    const coordsValid = validateCoordinates(values.longX, values.latY)
    if (!selectedTenant)
      missing.push({ step: 0, fieldKey: 'tenant', label: FIELD_LABELS.tenant })
    if (!coordsValid)
      missing.push({ step: 0, fieldKey: 'coords', label: FIELD_LABELS.coords })
    if (coordsValid && selectedTenant && isTenantCoordsOutOfBounds) {
      missing.push({
        step: 0,
        fieldKey: 'tenantBoundary',
        label: FIELD_LABELS.tenantBoundary,
      })
    }
    if (!values.typeSite_)
      missing.push({ step: 0, fieldKey: 'typeSite_', label: FIELD_LABELS.typeSite_ })
    if (!values.sitePlace_)
      missing.push({ step: 0, fieldKey: 'sitePlace_', label: FIELD_LABELS.sitePlace_ })
    if (!siteField?.selected?.id && !siteField?.selected)
      missing.push({ step: 0, fieldKey: 'site_', label: FIELD_LABELS['site_'] })
    if (!values.referenciaUbicacion_?.trim())
      missing.push({
        step: 0,
        fieldKey: 'referenciaUbicacion_',
        label: FIELD_LABELS.referenciaUbicacion_,
      })
    if (!values.modoCodigoPlusCode) {
      if (!siteAcronym?.selected?.id && !siteAcronym?.selected)
        missing.push({
          step: 0,
          fieldKey: 'siteAcronym',
          label: FIELD_LABELS.siteAcronym,
        })
      if (!sectionField?.selected?.id && !sectionField?.selected)
        missing.push({
          step: 0,
          fieldKey: 'sectionField',
          label: FIELD_LABELS.sectionField,
        })
    }

    // Step 1 — Datos del Árbol
    const { specieField, pseudonymField } = groupFieldAutocomplete
    if (!specieField?.selected?.id && !specieField?.selected)
      missing.push({ step: 1, fieldKey: 'specie', label: FIELD_LABELS.specie })
    if (!pseudonymField?.selected?.length)
      missing.push({ step: 1, fieldKey: 'pseudonyms', label: FIELD_LABELS.pseudonyms })
    const isAliveMissing =
      values.alive_ === undefined || values.alive_ === null || values.alive_ === ''
    if (isAliveMissing) missing.push({ step: 1, fieldKey: 'alive_', label: FIELD_LABELS.alive_ })
    const isTreeVerifiedMissing =
      values.treeVerified_ === undefined ||
      values.treeVerified_ === null ||
      values.treeVerified_ === ''
    if (isTreeVerifiedMissing)
      missing.push({
        step: 1,
        fieldKey: 'treeVerified_',
        label: FIELD_LABELS.treeVerified_,
      })

    // Step 2 — Datos Dasométricos
    const dasoFields = [
      'cap',
      'dap',
      'ht',
      'hc',
      'copaNS',
      'copaEW',
      'bifurcaciones',
      'inclArbol',
      'compInclArbol',
      'fusteRectitud',
      'espaciamiento',
    ]
    dasoFields.forEach(name => {
      const val = values[name]
      if (val === undefined || val === null || val === '') {
        missing.push({ step: 2, fieldKey: name, label: FIELD_LABELS[name] })
      }
    })

    // Step 3 — Estado Fitosanitario
    const phytoFields = [
      'follaje',
      'madurez',
      'floracion',
      'fructificacion',
      'calidadRaiz',
      'calidadFuste',
      'calidadCorteza',
      'calidadRamas',
      'calidadHojas',
      'calidadCima',
      'calidadCopa',
    ]
    phytoFields.forEach(name => {
      const val = values[name]
      if (val === undefined || val === null || val === '') {
        missing.push({ step: 3, fieldKey: name, label: FIELD_LABELS[name] })
      }
    })
    const hasDiseasePresence = temporal.some(item => item.category === 2)
    if (hasDiseasePresence && !temporal.some(item => item.category === 3)) {
      missing.push({
        step: 3,
        fieldKey: 'diseaseLocation',
        label: FIELD_LABELS.diseaseLocation,
      })
    }
    const hasPestPresence = temporal.some(item => item.category === 4)
    if (hasPestPresence && !temporal.some(item => item.category === 5)) {
      missing.push({
        step: 3,
        fieldKey: 'pestLocation',
        label: FIELD_LABELS.pestLocation,
      })
    }

    // Step 4 — Evaluación de Riesgos
    const riskFields = [
      { key: generalRiskFieldName, label: FIELD_LABELS.generalRisk },
      { key: 'rsgoCaida', label: FIELD_LABELS.rsgoCaida },
      { key: 'rsgoAfeccionConstr', label: FIELD_LABELS.rsgoAfeccionConstr },
      { key: 'rsgoInterfCirculacion', label: FIELD_LABELS.rsgoInterfCirculacion },
      { key: 'rsgoInfraestructura', label: FIELD_LABELS.rsgoInfraestructura },
      { key: 'rsgoRedesElectricas', label: FIELD_LABELS.rsgoRedesElectricas },
      { key: 'prioridadAtencion', label: FIELD_LABELS.prioridadAtencion },
    ]
    riskFields.forEach(({ key, label }) => {
      const val = values[key]
      if (val === undefined || val === null || val === '') {
        missing.push({ step: 4, fieldKey: key, label })
      }
    })

    // Estado de registro
    const estadoRegistro = normalizeRegistrationStatus(
      values.estadoRegistro,
      values.treeVerified_,
    )
    if (
      !(mode === 'create' && !isAdmin) &&
      estadoRegistro === REGISTRATION_STATUS.EN_REVISION &&
      cleanRevisionObservations(values.observacionesRevision).length === 0
    ) {
      missing.push({
        step: TOTAL_STEPS - 1,
        fieldKey: 'observacionesRevision',
        label: FIELD_LABELS.observacionesRevision,
      })
    }

    return missing
  }

  // Navigation Handlers
  const handleNext = async () => {
    const isValid = await validateStep(activeStep)
    if (!isValid) return
    const updated = [...completedSteps]
    updated[activeStep] = true
    setCompletedSteps(updated)
    setActiveStep(prev => Math.min(prev + 1, TOTAL_STEPS - 1))
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleBack = () => setActiveStep(prev => Math.max(prev - 1, 0))

  const collectChangedFields = () => {
    if (!formikRef.current) return []
    const current = formikRef.current.values
    const original = formikRef.current.initialValues
    const changed = []

    const COMPARABLE_FIELDS = [
      { key: 'longX', label: 'Longitud (X)' },
      { key: 'latY', label: 'Latitud (Y)' },
      { key: 'typeSite_', label: 'Tipo de zona' },
      { key: 'sitePlace_', label: 'Lugar del sitio' },
      { key: 'referenciaUbicacion_', label: 'Referencia de ubicación' },
      { key: 'alive_', label: '¿Árbol vivo?' },
      { key: 'estadoRegistro', label: 'Estado de registro' },
      { key: 'cap', label: 'CAP (circunferencia)' },
      { key: 'dap', label: 'DAP (diámetro)' },
      { key: 'ht', label: 'Altura total (HT)' },
      { key: 'hc', label: 'Altura comercial (HC)' },
      { key: 'copaNS', label: 'Copa Norte-Sur' },
      { key: 'copaEW', label: 'Copa Este-Oeste' },
      { key: 'bifurcaciones', label: 'Bifurcaciones' },
      { key: 'inclArbol', label: 'Inclinación del fuste principal (grados)' },
      { key: 'compInclArbol', label: 'Compensación de inclinación' },
      { key: 'fusteRectitud', label: 'Rectitud del fuste' },
      { key: 'espaciamiento', label: 'Espaciamiento' },
      { key: 'follaje', label: 'Follaje (%)' },
      { key: 'madurez', label: 'Madurez' },
      { key: 'floracion', label: 'Floración' },
      { key: 'fructificacion', label: 'Fructificación' },
      { key: 'calidadRaiz', label: 'Calidad de raíz' },
      { key: 'calidadFuste', label: 'Calidad de fuste' },
      { key: 'calidadCorteza', label: 'Calidad de corteza' },
      { key: 'calidadRamas', label: 'Calidad de ramas' },
      { key: 'calidadHojas', label: 'Calidad de hojas' },
      { key: 'calidadCima', label: 'Calidad de cima' },
      { key: 'calidadCopa', label: 'Calidad de copa' },
      { key: generalRiskFieldName, label: 'Riesgo general' },
      { key: 'rsgoCaida', label: 'Riesgo de caída' },
      { key: 'rsgoAfeccionConstr', label: 'Afección por construcciones' },
      { key: 'rsgoInterfCirculacion', label: 'Interferencia con circulación' },
      { key: 'rsgoInfraestructura', label: 'Riesgo de infraestructura' },
      { key: 'rsgoRedesElectricas', label: 'Redes eléctricas' },
      { key: 'prioridadAtencion', label: 'Urgencia de intervención' },
      { key: 'commit_', label: 'Observaciones' },
      { key: 'recorded_', label: 'Registrado por' },
    ]

    for (const { key, label } of COMPARABLE_FIELDS) {
      const currStr = current[key] == null ? '' : String(current[key]).trim()
      const origStr = original[key] == null ? '' : String(original[key]).trim()
      if (currStr !== origStr) {
        changed.push({ key, label, from: origStr || '(vacío)', to: currStr || '(vacío)' })
      }
    }

    const originalStatus = normalizeRegistrationStatus(
      original.estadoRegistro,
      original.treeVerified_,
    )
    const currentStatus = normalizeRegistrationStatus(
      current.estadoRegistro,
      current.treeVerified_,
    )
    if (originalStatus !== currentStatus) {
      const existingStatusChange = changed.find(item => item.key === 'estadoRegistro')
      if (existingStatusChange) {
        existingStatusChange.from = REGISTRATION_STATUS_LABELS[originalStatus]
        existingStatusChange.to = REGISTRATION_STATUS_LABELS[currentStatus]
      }
    }

    const originalReviewObs = JSON.stringify(cleanRevisionObservations(original.observacionesRevision))
    const currentReviewObs = JSON.stringify(cleanRevisionObservations(current.observacionesRevision))
    if (originalReviewObs !== currentReviewObs) {
      const formatReviewObs = value => {
        const items = cleanRevisionObservations(value)
        return items.length
          ? items.map((item, index) => `${index + 1}. ${item.texto}`).join(' | ')
          : '(vacío)'
      }
      changed.push({
        key: 'observacionesRevision',
        label: 'Correcciones',
        from: formatReviewObs(original.observacionesRevision),
        to: formatReviewObs(current.observacionesRevision),
      })
    }

    // Autocomplete fields
    const origAuto = originalAutocompleteRef.current || {}
    const { specieField, siteField, pseudonymField } = groupFieldAutocomplete

    const origSpecieId = String(
      origAuto.specieField?.selected?.id ?? origAuto.specieField?.selected ?? '',
    )
    const currSpecieId = String(specieField?.selected?.id ?? specieField?.selected ?? '')
    if (origSpecieId !== currSpecieId) {
      const origName =
        origAuto.specieField?.selected?.name ||
        origAuto.specieField?.selected?.label ||
        origSpecieId
      const currName =
        specieField?.selected?.name || specieField?.selected?.label || currSpecieId
      changed.push({
        key: 'specie_',
        label: 'Especie',
        from: origName || '(vacío)',
        to: currName || '(vacío)',
      })
    }

    const origPseudonyms = (origAuto.pseudonymField?.selected || [])
      .map(p => p.name || p.id || p)
      .join(', ')
    const currPseudonyms = (pseudonymField?.selected || [])
      .map(p => p.name || p.id || p)
      .join(', ')
    if (origPseudonyms !== currPseudonyms) {
      changed.push({
        key: 'pseudonym_',
        label: 'Nombres comunes',
        from: origPseudonyms || '(vacío)',
        to: currPseudonyms || '(vacío)',
      })
    }

    const origSiteId = String(
      origAuto.siteField?.selected?.id ?? origAuto.siteField?.selected ?? '',
    )
    const currSiteId = String(siteField?.selected?.id ?? siteField?.selected ?? '')
    if (origSiteId !== currSiteId) {
      const origSiteName =
        origAuto.siteField?.selected?.name ||
        origAuto.siteField?.selected?.label ||
        origSiteId
      const currSiteName =
        siteField?.selected?.name || siteField?.selected?.label || currSiteId
      changed.push({
        key: 'site_',
        label: 'Sitio',
        from: origSiteName || '(vacío)',
        to: currSiteName || '(vacío)',
      })
    }

    // Tenant and Canton
    if (mode === 'edit' && treeData) {
      const originalTenantId = treeData?.tenantId ?? null
      const allCities = getEnabledCities()

      let originalTenant = null
      let originalCantonSlug = null
      let originalCantonName = null
      for (const city of allCities) {
        const match = getTenantsByCantonSlug(city.slug).find(
          t => String(t.id) === String(originalTenantId),
        )
        if (match) {
          originalTenant = match
          originalCantonSlug = city.slug
          originalCantonName = city.fullName || city.name || city.slug
          break
        }
      }

      let currentTenant = null
      let currentCantonSlug = null
      let currentCantonName = null
      for (const city of allCities) {
        const match = getTenantsByCantonSlug(city.slug).find(
          t => t.slug === selectedTenant,
        )
        if (match) {
          currentTenant = match
          currentCantonSlug = city.slug
          currentCantonName = city.fullName || city.name || city.slug
          break
        }
      }

      const origTenantLabel =
        originalTenant?.name || String(originalTenantId ?? '') || '(sin tenant)'
      const currTenantLabel = currentTenant?.name || selectedTenant || '(sin tenant)'
      if (String(originalTenantId ?? '') !== String(currentTenant?.id ?? '')) {
        changed.push({
          key: 'tenant',
          label: 'Tenant',
          from: origTenantLabel,
          to: currTenantLabel,
        })
      }

      if (
        originalCantonSlug &&
        currentCantonSlug &&
        originalCantonSlug !== currentCantonSlug
      ) {
        changed.push({
          key: 'canton',
          label: 'Cantón',
          from: originalCantonName || originalCantonSlug,
          to: currentCantonName || currentCantonSlug,
        })
      }
    }

    return changed
  }

  const handleSubmitIntent = async () => {
    const missing = collectAllMissingFields()

    if (formikRef.current) {
      const { longX, latY } = formikRef.current.values
      if (!selectedTenant) {
        const alreadyIncluded = missing.some(field => field.fieldKey === 'tenant')
        if (!alreadyIncluded) {
          missing.push({
            step: 0,
            fieldKey: 'tenant',
            label: FIELD_LABELS.tenant,
          })
        }
      }
      const tenantBoundaryValidation = await validateCoordinatesAgainstTenant(longX, latY, {
        showAlert: false,
      })
      if (!tenantBoundaryValidation.isValid) {
        const alreadyIncluded = missing.some(field => field.fieldKey === 'tenantBoundary')
        if (!alreadyIncluded) {
          missing.push({
            step: 0,
            fieldKey: 'tenantBoundary',
            label: tenantBoundaryValidation.message || FIELD_LABELS.tenantBoundary,
          })
        }
      }
    }

    if (missing.length > 0) {
      setMissingFields(missing)
      setMissingFieldsOpen(true)
      return
    }

    // Aviso si no se alcanza el mínimo de fotos (continúa solo si el usuario acepta)
    if (photos.length < MIN_PHOTOS) {
      setPhotoWarningOpen(true)
      return
    }

    proceedToConfirm()
  }

  const proceedToConfirm = () => {
    if (mode === 'edit') {
      const changed = collectChangedFields()
      setChangedFields(changed)
      setChangesConfirmOpen(true)
      return
    }
    setConfirmOpen(true)
  }

  const handlePhotoWarningContinue = () => {
    setPhotoWarningOpen(false)
    proceedToConfirm()
  }

  const handleConfirmSubmit = async () => {
    const errors = (await formikRef.current?.validateForm()) || {}
    const errorFields = Object.keys(errors)

    // Si la validación Yup falla, surface el motivo en vez de guardar en silencio
    if (errorFields.length > 0) {
      setConfirmOpen(false)
      setChangesConfirmOpen(false)
      formikRef.current?.setTouched(
        errorFields.reduce((acc, name) => ({ ...acc, [name]: true }), {}),
      )
      const firstField = errorFields[0]
      const stepWithError = Object.keys(STEP_FORMIK_FIELDS).find(step =>
        (STEP_FORMIK_FIELDS[step] || []).includes(firstField),
      )
      if (stepWithError !== undefined) setActiveStep(Number(stepWithError))
      const fieldLabel = FIELD_LABELS[firstField] || firstField
      alertRef?.current?.message(
        `No se puede guardar: revise el campo "${fieldLabel}" (${errors[firstField]}).`,
      )
      alertRef?.current?.isSeverity('error')
      alertRef?.current?.openAlert(true)
      return
    }

    setConfirmOpen(false)
    setChangesConfirmOpen(false)
    formikRef.current?.submitForm()
  }

  const handleCancel = () => requestExit()

  const handleExitKeepEditing = () => {
    setExitConfirmOpen(false)
  }

  const handleExitWithoutSave = () => leaveForm({ keepDraft: false })

  const handleExitWithDraft = () => leaveForm({ keepDraft: true })

  const handleSaveDraft = () => {
    const saved = persistDraft()
    if (alertRef.current) {
      alertRef.current.message(
        saved
          ? 'Borrador guardado localmente'
          : 'No se pudo guardar el borrador en este momento',
      )
      alertRef.current.isSeverity(saved ? 'success' : 'warning')
      alertRef.current.openAlert(true)
    }
  }

  // Map component for LocationStep.
  // In edit mode, isAdmin=true disables canton maxBounds so the map can navigate
  // freely to the tree's actual location even if it differs from the URL canton.
  const mapComponent = (
    <MapboxMap
      isAdmin={mode === 'edit'}
      isAuth={true}
      pathname={location.pathname}
      widthMap="100%"
      heightMap="100%"
      store={store}
      minimalControls
      forcedTreeCoords={editTreeFixedCoords}
    />
  )

  const renderContent = () => (
    <>
      {/* LocationStep stays mounted always to preserve Mapbox instance */}
      <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
        <LocationStep
          itemsGeneral={itemsGeneral}
          itemsAttributes={itemsAttributes}
          mapComponent={mapComponent}
          isMobile={isMobile}
          zoneTypesData={zoneTypesData}
          allSiteTypes={allSiteTypes}
          siteTypesData={siteTypesData}
          editMode={mode === 'edit'}
          treeData={treeData}
          existingCode={treeData?.codigo || treeData?.code}
          existingCodeMode={treeData?.codeMode}
          existingGoogleCode={treeData?.googleCode}
          existingGoogleCodeSuffix={treeData?.googleCodeSuffix}
          coordinatesOutOfTenantBounds={isTenantCoordsOutOfBounds}
        />
      </Box>
      <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
        <TreeDataStep itemsGeneral={itemsGeneral} mode={mode} treeData={treeData} />
      </Box>
      <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
        <DasometricStep itemsAttributes={itemsAttributes} />
      </Box>
      <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
        <PhytosanitaryStep
          itemsAttributes={itemsAttributes}
          itemsInconvenience={itemsInconvenience}
          inconvenienceErrors={inconvenienceErrors}
        />
      </Box>
      <Box sx={{ display: activeStep === 4 ? 'block' : 'none' }}>
        <RiskStep
          itemsInconvenience={itemsInconvenience}
          generalRiskFieldName={generalRiskFieldName}
        />
      </Box>
      <Box sx={{ display: activeStep === 5 ? 'block' : 'none' }}>
        <SummaryStep
          itemsGeneral={itemsGeneral}
          itemsInconvenience={itemsInconvenience}
          groupFieldAutocomplete={groupFieldAutocomplete}
          mode={mode}
          zoneTypesData={zoneTypesData}
          allSiteTypes={allSiteTypes}
          siteTypesData={siteTypesData}
          onEditStep={step => setActiveStep(step)}
          photos={photos}
          onPhotosChange={setPhotos}
          primaryPhotoIndex={primaryPhotoIndex}
          onPrimaryPhotoChange={setPrimaryPhotoIndex}
          maxPhotos={MAX_PHOTOS}
          minPhotos={MIN_PHOTOS}
          onDeleteExisting={photoId => setDeletedPhotoIds(prev => [...prev, photoId])}
        />
      </Box>
    </>
  )

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: marginMenu,
        mb: { xs: 10, md: 4 },
        px: { xs: 0, md: 3 },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={formik.FORM_VALIDATION}
        onSubmit={async values => {
          const { specieField, siteField, sectionField, pseudonymField, siteAcronym } =
            groupFieldAutocomplete
          const combinedValues = {
            ...values,
            family_: specieField?.selected?.id || values.family_ || '',
            specie_: specieField?.selected?.id || values.specie_ || '',
            pseudonym_:
              pseudonymField?.selected?.map(p => p.name || p.id || p) ||
              values.pseudonym_ ||
              [],
            site_:
              siteField?.selected?.id ||
              siteField?.selected?.label ||
              siteField?.selected ||
              values.site_ ||
              '',
            siteAcronym_:
              siteAcronym?.selected?.id ||
              siteAcronym?.selected?.label ||
              siteAcronym?.selected ||
              values.siteAcronym_ ||
              '',
            section_:
              sectionField?.selected?.id ||
              sectionField?.selected?.label ||
              sectionField?.selected ||
              values.section_ ||
              '',
            prioridadAtencion: values.prioridadAtencion || null,
            codigoManual: values.codigoManual || null,
          }
          const submittedEstadoRegistro =
            mode === 'create' && !isAdmin
              ? REGISTRATION_STATUS.NO_VERIFICADO
              : normalizeRegistrationStatus(
                  combinedValues.estadoRegistro,
                  combinedValues.treeVerified_,
                )
          combinedValues.estadoRegistro = submittedEstadoRegistro
          combinedValues.treeVerified_ =
            submittedEstadoRegistro === REGISTRATION_STATUS.VERIFICADO ? 1 : 0
          combinedValues.observacionesRevision =
            submittedEstadoRegistro === REGISTRATION_STATUS.EN_REVISION
              ? cleanRevisionObservations(combinedValues.observacionesRevision)
              : []

          try {
            if (!selectedTenant) {
              showBoundaryAlert('Debe seleccionar una institución (tenant) para guardar.')
              return
            }

            const tenantBoundaryValidation = await validateCoordinatesAgainstTenant(
              combinedValues.longX,
              combinedValues.latY,
              { showAlert: true },
            )
            if (!tenantBoundaryValidation.isValid) return

            // ── Edit mode ──
            if (mode === 'edit') {
              // Search across all cantons — the tree may be reassigned to a different canton
              const allCities = getEnabledCities()
              let editTenantId = null
              for (const city of allCities) {
                const match = getTenantsByCantonSlug(city.slug).find(
                  t => t.slug === selectedTenant,
                )
                if (match) {
                  editTenantId = match.id ?? null
                  break
                }
              }

              const result = await submitTreeUpdateV2({
                values: combinedValues,
                treeData,
                temporal,
                stateInitial: formik.ITEMS_INCONVENIENCE_FORMIK,
                groupsFields: groupFieldAutocomplete,
                photos,
                deletedPhotoIds,
                token: getToken(),
                selectedTenantId: editTenantId,
                initialValues: formikRef.current?.initialValues ?? null,
              })
              if (result?.stored) {
                clearDraft()
              }
              setIsStored(result)
              return
            }

            // ── Create mode ──
            const lng = parseFloat(combinedValues.longX)
            const lat = parseFloat(combinedValues.latY)

            let result = null
            const pendingPhotoTreeId = pendingPhotoTreeRef.current

            if (createdTreeErrorRef.current) {
              setIsStored({
                stored: false,
                treeCreated: true,
                treeId: createdTreeErrorRef.current.treeId,
                message: createdTreeErrorRef.current.message,
                operationReport: createdTreeErrorRef.current.operationReport,
              })
              return
            }

            if (pendingPhotoTreeId) {
              result = {
                stored: true,
                treeId: pendingPhotoTreeId,
                treeCode: treeCodeRef.current,
              }
            } else {
              // Envío único: árbol + features + inconvenientes en un solo JSON
              // El tenantId se obtiene del JWT (activate-tenant ya lo embebió)
              result = await submitTreeRegistrationV2({
                values: combinedValues,
                temporal,
                stateInitial: formik.ITEMS_INCONVENIENCE_FORMIK,
                groupsFields: groupFieldAutocomplete,
                token: getToken(),
                lnglat: [lng, lat],
              })
            }

            let finalResult = result
            if (result?.treeCode) treeCodeRef.current = result.treeCode || null

            // Fotos: envío separado por ser multipart/form-data.
            // Si falla, se conserva el árbol creado y el siguiente guardado reintenta
            // solo las fotos contra el mismo treeId para evitar duplicados.
            if (result?.stored && photos.length > 0 && result.treeId) {
              const orderedPhotos =
                primaryPhotoIndex > 0 && primaryPhotoIndex < photos.length
                  ? [
                      photos[primaryPhotoIndex],
                      ...photos.filter((_, i) => i !== primaryPhotoIndex),
                    ]
                  : photos
              const photoResult = await uploadTreePhotos(result.treeId, orderedPhotos, getToken())
              if (photoResult?.saved !== true) {
                pendingPhotoTreeRef.current = result.treeId
                finalResult = {
                  ...result,
                  stored: false,
                  treeCreated: true,
                  photosPending: true,
                  message:
                    photoResult?.message ||
                    'Árbol registrado, pero las fotos quedaron pendientes de subir.',
                  operationReport: {
                    action: 'created',
                    ok: false,
                    treeId: result.treeId,
                    report: {
                      ...(result.operationReport?.report || {}),
                      photos: {
                        ok: false,
                        message:
                          photoResult?.message ||
                          'Las fotos quedaron pendientes de subir.',
                        saved: [],
                        failed: orderedPhotos.map(photo => ({
                          name: photo.originalName || photo.file?.name || 'foto',
                          message:
                            photoResult?.message ||
                            'No se pudo subir la foto.',
                        })),
                      },
                    },
                  },
                }
              }
            }

            if (
              finalResult?.stored === false &&
              finalResult?.treeCreated &&
              !finalResult?.photosPending
            ) {
              createdTreeErrorRef.current = {
                treeId: finalResult.treeId,
                message:
                  finalResult.message ||
                  'El árbol fue creado, pero quedó pendiente una sincronización del backend. Evite volver a registrarlo.',
                operationReport: finalResult.operationReport,
              }
            }

            if (finalResult?.stored) {
              pendingPhotoTreeRef.current = null
              createdTreeErrorRef.current = null
              dispatch(setTreeCoords(null))
              dispatch(setAddMode(false))
              clearDraft()
              setPhotos([])
              setPrimaryPhotoIndex(0)
            }
            setIsStored(finalResult)
          } catch (e) {
            console.error('Error en registro:', e)
            setIsStored({
              stored: false,
              message: `Error inesperado: ${e?.message || 'desconocido'}`,
            })
          }
        }}>
        <Form>
          <DraftSync
            draftLoaded={draftLoaded}
            draftKey={activeDraftKey}
            activeStep={activeStep}
            completedSteps={completedSteps}
            groupFieldAutocomplete={groupFieldAutocomplete}
            mode={mode}
          />
          <UnsavedChangesSync
            mode={mode}
            coords={coords}
            photos={photos}
            deletedPhotoIds={deletedPhotoIds}
            onUnsavedChange={handleUnsavedChange}
          />
          <CoordinatesTenantValidationSync
            selectedTenant={selectedTenant}
            tenantBoundaryData={tenantBoundaryData}
            tenantBoundaryLoading={tenantBoundaryLoading}
            tenantIsGeneric={tenantIsGeneric}
            onStatusChange={handleTenantBoundsStatusChange}
          />

          {mode === 'edit' && treeInactive && (
            <Alert severity="warning" variant="filled" sx={{ mb: 2 }}>
              <AlertTitle>Árbol deshabilitado</AlertTitle>
              Este árbol está deshabilitado o eliminado lógicamente. Puedes revisar y editar
              sus datos, pero no aparecerá en el mapa público mientras siga en este estado.
            </Alert>
          )}

          {/* Mobile progress bar — CSS-hidden on desktop to keep Map always mounted */}
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'sticky',
              top: progressStickyTop,
              zIndex: theme => theme.zIndex.appBar - 1,
              bgcolor: 'background.paper',
            }}>
            <WizardProgressMobile
              steps={STEPS}
              activeStep={activeStep}
              totalSteps={TOTAL_STEPS}
              onStepClick={step => setActiveStep(step)}
              completedSteps={completedSteps}
              mode={mode}
            />
          </Box>

          <Grid container spacing={3}>
            {/* Stepper Sidebar — CSS-hidden on mobile */}
            <Grid
              item
              md={3}
              sx={{
                display: { xs: 'none', md: 'block' },
                position: { md: 'sticky' },
                top: { md: progressStickyTop },
                alignSelf: 'flex-start',
                height: 'fit-content',
              }}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="primary.main"
                    gutterBottom>
                    {mode === 'edit' ? 'Progreso de edición' : 'Progreso de registro'}
                  </Typography>

                  <Stepper activeStep={activeStep} orientation="vertical">
                    {STEPS.map((step, index) => (
                      <Step key={step.label} completed={completedSteps[index]}>
                        <StepLabel
                          icon={
                            completedSteps[index] ? (
                              <CheckIcon color="success" />
                            ) : (
                              step.icon
                            )
                          }
                          onClick={() => setActiveStep(index)}
                          sx={{ cursor: 'pointer' }}>
                          <Typography sx={{ fontSize: '1em', fontWeight: 600 }}>
                            {step.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary">
                            {step.description}
                          </Typography>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </CardContent>
              </Card>
            </Grid>

            {/* Main Content — always mounted at this position to keep Map alive */}
            <Grid item xs={12} md={9}>
              <Card
                elevation={isMobile ? 0 : 3}
                sx={{
                  minHeight: { md: '600px' },
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                {/* Card header — CSS-hidden on mobile */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {STEPS[activeStep].icon}
                    <Typography variant="h6" color="primary.main" fontWeight={600}>
                      {STEPS[activeStep].label}
                    </Typography>
                  </Box>
                  <Button
                    type="button"
                    size="small"
                    color="inherit"
                    onClick={handleCancel}
                    startIcon={<CancelIcon />}>
                    {mode === 'edit' ? 'Cancelar Edición' : 'Cancelar Registro'}
                  </Button>
                </Box>

                <CardContent sx={{ flex: 1, overflowY: 'auto' }}>
                  {renderContent()}
                </CardContent>

                <Divider sx={{ display: { xs: 'none', md: 'block' } }} />
                <CardActions
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'space-between',
                    p: 2,
                  }}>
                  <Button
                    type="button"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    startIcon={<NavigateBeforeIcon />}>
                    Anterior
                  </Button>
                  <Box>
                    {activeStep === TOTAL_STEPS - 1 ? (
                      <Button
                        type="button"
                        variant="contained"
                        color="success"
                        onClick={handleSubmitIntent}
                        startIcon={<SaveIcon />}>
                        {mode === 'edit' ? 'Guardar Cambios' : 'Finalizar y Guardar'}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<NavigateNextIcon />}>
                        Siguiente
                      </Button>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          </Grid>

          {/* FAB Estado de registro — siempre disponible en edición */}
          {mode === 'edit' && (
            <RegistrationStatusFab
              open={statusDialogOpen}
              onOpen={() => setStatusDialogOpen(true)}
              onClose={() => setStatusDialogOpen(false)}
            />
          )}

          {/* FAB Observaciones — siempre disponible en edición; en registro se oculta en resumen */}
          {(mode === 'edit' || activeStep < TOTAL_STEPS - 1) && (
            <CommitFab
              open={commitDialogOpen}
              onOpen={() => setCommitDialogOpen(true)}
              onClose={() => setCommitDialogOpen(false)}
            />
          )}

          {/* Mobile navigation — CSS-hidden on desktop (position:fixed inside, display:none hides it) */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <WizardNavigationMobile
              activeStep={activeStep}
              totalSteps={TOTAL_STEPS}
              onNext={handleNext}
              onBack={handleBack}
              onSaveDraft={handleSaveDraft}
              onSubmitIntent={handleSubmitIntent}
              isDraftSaved={false}
              mode={mode}
            />
          </Box>
        </Form>
      </Formik>

      {/* Exit Confirmation Dialog */}
      <Dialog
        open={exitConfirmOpen}
        onClose={handleExitKeepEditing}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>
          {mode === 'edit' ? '¿Salir de la edición?' : '¿Salir del registro?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {hasUnsavedData
              ? 'Hay datos sin guardar. Puedes salir sin guardar, guardar un borrador local o continuar en el formulario.'
              : 'Puedes continuar en el formulario o salir.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ gap: 1, px: 3, pb: 2, flexWrap: 'wrap' }}>
          <Button type="button" variant="outlined" onClick={handleExitKeepEditing}>
            No salir
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="error"
            onClick={handleExitWithoutSave}>
            Salir sin guardar
          </Button>
          <Button type="button" variant="contained" onClick={handleExitWithDraft}>
            Guardar borrador y salir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Missing Fields Dialog */}
      <Dialog
        open={missingFieldsOpen}
        onClose={() => setMissingFieldsOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Campos obligatorios incompletos</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Completa los siguientes campos antes de guardar. Haz clic en{' '}
            <strong>Ir al paso</strong> para navegar directamente.
          </DialogContentText>
          {STEPS.map((_, stepIdx) => {
            const stepMissing = missingFields.filter(f => f.step === stepIdx)
            if (!stepMissing.length) return null
            return (
              <Box key={stepIdx} sx={{ mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  color="primary.main"
                  fontWeight={700}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {STEPS[stepIdx].icon}
                  {STEPS[stepIdx].label}
                </Typography>
                <List dense disablePadding>
                  {stepMissing.map(f => (
                    <ListItem
                      key={f.fieldKey}
                      disableGutters
                      secondaryAction={
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setActiveStep(stepIdx)
                            setMissingFieldsOpen(false)
                          }}>
                          Ir al paso
                        </Button>
                      }>
                      <ListItemText
                        primary={`• ${f.label}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )
          })}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setMissingFieldsOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {mode === 'edit' ? 'Confirmar actualización' : 'Confirmar registro'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {mode === 'edit'
              ? '¿Está seguro de que desea guardar los cambios en este árbol?'
              : '¿Está seguro de que desea guardar este árbol? Esta acción no se puede deshacer.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setConfirmOpen(false)}>
            Corregir
          </Button>
          <Button
            type="button"
            variant="contained"
            color="success"
            onClick={handleConfirmSubmit}
            startIcon={<SaveIcon />}>
            {mode === 'edit' ? 'Confirmar y Actualizar' : 'Confirmar y Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Changed Fields Confirmation Dialog — edit mode only */}
      <Dialog
        open={changesConfirmOpen}
        onClose={() => setChangesConfirmOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle color="primary" fontWeight={600}>
          Confirmar cambios
        </DialogTitle>
        <DialogContent>
          {changedFields.length === 0 ? (
            <DialogContentText>
              No se detectaron cambios en los campos del formulario. ¿Desea guardar de
              todas formas?
            </DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Se modificaron <strong>{changedFields.length}</strong> campo(s):
              </DialogContentText>
              <List dense disablePadding>
                {changedFields.map(f => (
                  <ListItem
                    key={f.key}
                    disableGutters
                    sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                      {f.label}
                    </Typography>
                    <Box
                      display="flex"
                      gap={1}
                      alignItems="center"
                      flexWrap="wrap"
                      mt={0.25}>
                      <Typography
                        variant="body2"
                        color="error.main"
                        sx={{ textDecoration: 'line-through' }}>
                        {f.from}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        →
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        {f.to}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setChangesConfirmOpen(false)}>
            Corregir
          </Button>
          <Button
            type="button"
            variant="contained"
            color="success"
            onClick={handleConfirmSubmit}
            startIcon={<SaveIcon />}>
            Confirmar y Actualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Minimum Photos Warning Dialog */}
      <Dialog
        open={photoWarningOpen}
        onClose={() => setPhotoWarningOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle color="warning.main" fontWeight={600}>
          Fotos por debajo del mínimo
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se recomiendan al menos <strong>{MIN_PHOTOS}</strong> fotografías y has
            adjuntado <strong>{photos.length}</strong>. ¿Deseas guardar de todas formas
            omitiendo el mínimo recomendado?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setPhotoWarningOpen(false)}>
            Agregar fotos
          </Button>
          <Button
            type="button"
            variant="contained"
            color="warning"
            onClick={handlePhotoWarningContinue}>
            Guardar de todas formas
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alerts and Backdrops */}
      <AlertCustom ref={alertRef} />
      {mode !== 'edit' && (
        <Backdrop
          ref={backdropRef}
          treeId={isStored?.treeId}
          canton={currentCity}
          tenant={selectedTenant}
          onSuccess={() => {
            const code = treeCodeRef.current
            navigate(
              code
                ? buildCantonUrl(currentCity, cantones, `/${code}`)
                : buildCantonUrl(currentCity, cantones, '/home'),
              { replace: true },
            )
          }}
        />
      )}
    </Container>
  )
}

const STATUS_FAB_COLOR = {
  [REGISTRATION_STATUS.VERIFICADO]: 'success',
  [REGISTRATION_STATUS.EN_REVISION]: 'warning',
  [REGISTRATION_STATUS.NO_VERIFICADO]: 'error',
}

const RegistrationStatusFab = ({ open, onOpen, onClose }) => {
  const { values } = useFormikContext()
  const status = normalizeRegistrationStatus(values.estadoRegistro, values.treeVerified_)
  const correctionsCount = cleanRevisionObservations(values.observacionesRevision).length
  const StatusIcon = STATUS_META[status]?.icon || ErrorOutlineIcon

  return (
    <>
      {/* FAB encima del de Observaciones */}
      <Badge
        color="warning"
        badgeContent={correctionsCount}
        invisible={correctionsCount === 0}
        sx={{ position: 'fixed', bottom: { xs: 144, md: 96 }, right: 24, zIndex: 1200 }}>
        <Fab
          color={STATUS_FAB_COLOR[status] || 'default'}
          size="medium"
          onClick={onOpen}
          title="Estado de registro y correcciones">
          <StatusIcon />
        </Fab>
      </Badge>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
          color="primary">
          <StatusIcon color="primary" />
          Estado de registro
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Define el estado del registro. En revisión, agrega las correcciones que el técnico de
            campo debe realizar.
          </DialogContentText>
          <RegistrationStatusPanel container="card" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={onClose}>
            Listo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const CommitFab = ({ open, onOpen, onClose }) => {
  const { values, setFieldValue } = useFormikContext()
  const hasText = Boolean(values.commit_?.trim())
  const [confirmClear, setConfirmClear] = React.useState(false)

  return (
    <>
      {/* FAB fijo abajo a la derecha */}
      <Badge
        color="success"
        variant="dot"
        invisible={!hasText}
        sx={{ position: 'fixed', bottom: { xs: 80, md: 32 }, right: 24, zIndex: 1200 }}>
        <Fab
          color="primary"
          size="medium"
          onClick={onOpen}
          title="Observaciones del registro">
          <CommentIcon />
        </Fab>
      </Badge>

      {/* Dialog centrado */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
          color="primary">
          <CommentIcon color="primary" />
          Observaciones
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Escribe observaciones generales sobre el registro o edición de este árbol.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={7}
            label="Observaciones"
            placeholder="Ej: El árbol presenta inclinación leve hacia el norte. Se tomaron medidas en época seca..."
            value={values.commit_ || ''}
            onChange={e => setFieldValue('commit_', e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText={`${(values.commit_ || '').length} / 500 caracteres`}
            variant="outlined"
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          {hasText ? (
            <Button
              size="small"
              color="error"
              variant={confirmClear ? 'contained' : 'text'}
              onClick={() => {
                if (!confirmClear) {
                  setConfirmClear(true)
                } else {
                  setFieldValue('commit_', '')
                  setConfirmClear(false)
                }
              }}
              onBlur={() => setConfirmClear(false)}>
              {confirmClear ? '¿Confirmar limpieza?' : 'Limpiar'}
            </Button>
          ) : (
            <Box />
          )}
          <Button variant="contained" onClick={onClose}>
            Listo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TreeRegistrationWizardV2
